// Web Push / VAPID plumbing for the Lately web client (RFC 8292, RFC 9749).
//
// The applicationServerKey a PushSubscription must be created with is the
// server's VAPID public key. Stalwart >= 0.16.14 advertises it in the JMAP
// session under the capability `urn:ietf:params:jmap:webpush-vapid`, and the
// shared Swift core carries the session through the shell snapshot
// (StoredAccount.session.session.capabilities — JMAPKit's JMAPSession, which
// also exposes it as `webPushApplicationServerKey`). This module reads it from
// there and from nowhere else: the key is per-server state, so when a server
// does not advertise one there is nothing to subscribe with and Web Push is
// simply not offered (`null`). There is deliberately no build-time fallback —
// a key baked into the bundle can never be verified against the server that
// will sign the pushes, and a subscription made with the wrong key is silently
// dead.
//
// The one knob is a dev-only pin: `VITE_LATELY_VAPID_PUBLIC_KEY_DEV` (honoured
// only when `import.meta.env.DEV` is true, i.e. `vite dev`, never in a
// production build). It cannot introduce a key; it asserts that the key the
// server advertises is the one the fixture was expected to run with, so a
// stale local Stalwart state dir fails loudly instead of subscribing to a
// server whose private key does not match. If it disagrees, the answer is
// `null` and a console warning.
//
// Nothing here touches PushManager or the service worker: it answers the one
// question "which key do I subscribe with for this account/server?".

import type { JMAPSession, MailShellSnapshot, StoredAccount } from "./shell-types";

export const WEBPUSH_VAPID_CAPABILITY = "urn:ietf:params:jmap:webpush-vapid";

export interface WebPushVAPIDCapability {
  // base64url (no padding) uncompressed P-256 point: 65 bytes / 87 characters.
  applicationServerKey: string;
}

// The only source is the JMAP session. Kept as a discriminator so callers that
// log or display provenance do not have to change if a second source ever
// appears (it should not).
export type ApplicationServerKeySource = "session";

export interface ApplicationServerKey {
  key: string;
  source: ApplicationServerKeySource;
}

// Resolution options. `devOverride` is the dev-only pin described above; the
// default reads it from Vite's env and only when running under `vite dev`.
export interface ApplicationServerKeyOptions {
  devOverride?: string | null;
  warn?: (message: string) => void;
}

function isValidKey(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && /^[A-Za-z0-9_-]+$/.test(value);
}

// The capability object from a session, or null when the server does not
// advertise it (Stalwart < 0.16.14, or `Jmap.webPushKey` unset).
export function webPushVAPIDCapability(
  session: JMAPSession | null | undefined,
): WebPushVAPIDCapability | null {
  const raw = session?.capabilities?.[WEBPUSH_VAPID_CAPABILITY];
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const key = (raw as { applicationServerKey?: unknown }).applicationServerKey;
  return isValidKey(key) ? { applicationServerKey: key } : null;
}

// The dev-only pin from Vite's env: `VITE_LATELY_VAPID_PUBLIC_KEY_DEV`, and only
// under `vite dev`. Production builds always return null here, whatever the env
// says, so the pin can never leak into a deployed bundle's behaviour.
export function devApplicationServerKeyOverride(
  env: Record<string, unknown> = import.meta.env,
): string | null {
  if (env.DEV !== true) {
    return null;
  }
  const key = env.VITE_LATELY_VAPID_PUBLIC_KEY_DEV;
  return typeof key === "string" && isValidKey(key.trim()) ? key.trim() : null;
}

function defaultOptions(): ApplicationServerKeyOptions {
  return {
    devOverride: devApplicationServerKeyOverride(),
    warn: (message) => console.warn(message),
  };
}

// The key to subscribe with for this session, or `null` when Web Push cannot
// be offered: the server advertises no `urn:ietf:params:jmap:webpush-vapid`
// capability, or (dev only) the advertised key is not the pinned one.
export function applicationServerKeyForSession(
  session: JMAPSession | null | undefined,
  options: ApplicationServerKeyOptions = defaultOptions(),
): ApplicationServerKey | null {
  const advertised = webPushVAPIDCapability(session)?.applicationServerKey;
  if (!advertised) {
    return null;
  }
  const pinned = options.devOverride?.trim();
  if (pinned && pinned !== advertised) {
    options.warn?.(
      `webpush: the server's VAPID applicationServerKey (${advertised.slice(0, 12)}…) does not match ` +
        `VITE_LATELY_VAPID_PUBLIC_KEY_DEV (${pinned.slice(0, 12)}…); not offering Web Push. ` +
        "Reset the local Stalwart fixture or update the pin.",
    );
    return null;
  }
  return { key: advertised, source: "session" };
}

export function applicationServerKeyForAccount(
  account: StoredAccount | null | undefined,
  options: ApplicationServerKeyOptions = defaultOptions(),
): ApplicationServerKey | null {
  return applicationServerKeyForSession(account?.session?.session, options);
}

// The active account's key from a shell snapshot (what MailShell has in hand).
export function applicationServerKeyForSnapshot(
  snapshot: MailShellSnapshot | null | undefined,
  options: ApplicationServerKeyOptions = defaultOptions(),
): ApplicationServerKey | null {
  if (!snapshot) {
    return null;
  }
  const active =
    snapshot.accounts.find((account) => account.storageKey === snapshot.activeAccountStorageKey) ??
    snapshot.accounts.find((account) => account.isActive) ??
    null;
  return applicationServerKeyForAccount(active, options);
}

// PushManager.subscribe wants the key as a BufferSource.
export function applicationServerKeyBytes(key: string): Uint8Array {
  const base64 = key.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}
